import { z } from 'zod'

/**
 * Enums canônicos do domínio.
 * Fonte: docs/SDD/01-glossario.md e as decisões v1 de cada módulo.
 * Valores em inglês (schema); rótulos em pt-BR ficam na camada de UI.
 *
 * `/* @__PURE__ *\/` nos schemas: o bundler não sabe que `z.enum()` não tem
 * efeito colateral e, sem a marca, levava o zod inteiro para o JS inicial da
 * web, que só usa as listas e os tipos (Onda 6.2). Schema novo leva a marca.
 */

// --- Projeto -----------------------------------------------------------------

/** Q08 — o tipo controla campos e templates, não cria kanbans diferentes. */
export const PROJECT_TYPES = ['sistema', 'departamental', 'continuo'] as const
export const projectTypeSchema = /* @__PURE__ */ z.enum(PROJECT_TYPES)
export type ProjectType = z.infer<typeof projectTypeSchema>

/** Q42 — departamental por padrão; `restrito` exige membership explícita. */
export const PROJECT_VISIBILITIES = ['departamento', 'restrito'] as const
export const projectVisibilitySchema = /* @__PURE__ */ z.enum(PROJECT_VISIBILITIES)
export type ProjectVisibility = z.infer<typeof projectVisibilitySchema>

/** Ciclo de vida no kanban de projetos. `rascunho` é a 1ª coluna — cadastro incompleto. */
export const PROJECT_STATUSES = ['rascunho', 'ativo', 'pausado', 'arquivado'] as const
export const projectStatusSchema = /* @__PURE__ */ z.enum(PROJECT_STATUSES)
export type ProjectStatus = z.infer<typeof projectStatusSchema>

/**
 * Criticidade do projeto — etiqueta visual (campo `color` no Project).
 * Rótulos na UI: Baixa · Média · Alta · Crítica.
 */
export const PROJECT_CRITICALITIES = ['baixa', 'media', 'alta', 'critica'] as const
export const projectCriticalitySchema = /* @__PURE__ */ z.enum(PROJECT_CRITICALITIES)
export type ProjectCriticality = z.infer<typeof projectCriticalitySchema>

// --- Quadro ------------------------------------------------------------------

/**
 * Tipo da coluna. INVARIANTE: exatamente uma coluna `final` por quadro.
 * Entrar na coluna `final` dispara conclusão, crédito de GOLD e devolutiva.
 */
export const COLUMN_KINDS = ['inicial', 'intermediaria', 'final'] as const
export const columnKindSchema = /* @__PURE__ */ z.enum(COLUMN_KINDS)
export type ColumnKind = z.infer<typeof columnKindSchema>

// --- Atividade ---------------------------------------------------------------

/** Q09 — prioridade mede urgência e é independente da dificuldade 0–10. */
export const PRIORITIES = ['baixa', 'normal', 'alta', 'urgente'] as const
export const prioritySchema = /* @__PURE__ */ z.enum(PRIORITIES)
export type Priority = z.infer<typeof prioritySchema>

/** Tag de origem: marcador de sistema, não editável pelo usuário. */
export const ACTIVITY_ORIGINS = [
  'manual',
  'chamado',
  'erro_producao',
  'rotina',
  'github',
  'financeiro',
  // C11 — item de nota do Caderno transformado em atividade (migration 027).
  'caderno',
] as const
export const activityOriginSchema = /* @__PURE__ */ z.enum(ACTIVITY_ORIGINS)
export type ActivityOrigin = z.infer<typeof activityOriginSchema>

/** Q30 — bug é atividade com tipo próprio, não label do GitHub. */
export const ACTIVITY_TYPES = ['tarefa', 'bug', 'melhoria', 'incidente'] as const
export const activityTypeSchema = /* @__PURE__ */ z.enum(ACTIVITY_TYPES)
export type ActivityType = z.infer<typeof activityTypeSchema>

/**
 * Dificuldade: inteiro de 0 a 10, nulo até ser avaliada.
 * Q44 — confirmada por gestor/líder e CONGELADA ao entrar em andamento.
 */
export const difficultySchema = /* @__PURE__ */ z.number().int().min(0).max(10)

// --- Chamado -----------------------------------------------------------------

/**
 * Q02 — o enum do BANCO (`sistemas.ticket_status`). O contrato tinha uma
 * taxonomia própria (`novo`, `triagem`, `em_atendimento`…) que o banco nunca
 * aceitou; alinhado na onda 7 do ciclo de demandas. O valor original do
 * Portal continua em `status_raw`.
 */
export const TICKET_STATUSES = [
  'aberto',
  'em_andamento',
  'pausado',
  'resolvido',
  'cancelado',
] as const
export const ticketStatusSchema = /* @__PURE__ */ z.enum(TICKET_STATUSES)
export type TicketStatus = z.infer<typeof ticketStatusSchema>

export const TRIAGE_STATUSES = ['pendente', 'triado', 'recusado'] as const
export const triageStatusSchema = /* @__PURE__ */ z.enum(TRIAGE_STATUSES)
export type TriageStatus = z.infer<typeof triageStatusSchema>

/**
 * `portal`: espelho do Portal de Chamados. `hub`: registrado no Hub enquanto o
 * Portal não existe (ADR-0015) — migra para lá quando ele chegar.
 */
export const TICKET_SOURCES = ['portal', 'hub'] as const
export const ticketSourceSchema = /* @__PURE__ */ z.enum(TICKET_SOURCES)
export type TicketSource = z.infer<typeof ticketSourceSchema>

// --- Rotina ------------------------------------------------------------------

/** Q14 — catálogo inicial; nome, horário e participantes são configuráveis. */
export const ROUTINE_KINDS = [
  'alinhamento_semanal',
  'objetivos_mensais',
  'prova_mensal',
  'coleta_feedback',
  'entrega_feedback',
  'um_a_um',
  'revisao_processos',
  'outro',
] as const
export const routineKindSchema = /* @__PURE__ */ z.enum(ROUTINE_KINDS)
export type RoutineKind = z.infer<typeof routineKindSchema>

/**
 * Cadência da rotina.
 *
 * - `semanal` — toda terça-feira.
 * - `mensal` — dia 20 de cada mês.
 * - `mensal_ordinal` — a primeira terça-feira do mês, a última sexta.
 *
 * O ordinal existe porque é assim que boa parte das cerimônias é marcada de
 * verdade: "primeira segunda do mês" mantém o dia da semana fixo, e o numeral
 * do dia é que varia. Expressar isso como "dia 3" só funciona até o mês virar.
 *
 * O banco segue exigindo exatamente um entre `weekday` e `month_day`
 * (`routine_one_cadence`); o ordinal é `weekday` + `week_of_month`.
 */
export const ROUTINE_CADENCES = ['semanal', 'mensal', 'mensal_ordinal'] as const
export const routineCadenceSchema = /* @__PURE__ */ z.enum(ROUTINE_CADENCES)
export type RoutineCadence = z.infer<typeof routineCadenceSchema>

/**
 * Semana do mês numa cadência ordinal. `-1` é "a última", que não é o mesmo
 * que a quarta: em mês de cinco terças, a última é a quinta ocorrência.
 */
export const ROUTINE_WEEKS_OF_MONTH = [1, 2, 3, 4, -1] as const
export type RoutineWeekOfMonth = (typeof ROUTINE_WEEKS_OF_MONTH)[number]

/** Q14 — política para quando a ocorrência cai em dia não útil. */
export const NON_BUSINESS_DAY_POLICIES = ['proximo', 'anterior', 'pular'] as const
export const nonBusinessDayPolicySchema = /* @__PURE__ */ z.enum(NON_BUSINESS_DAY_POLICIES)
export type NonBusinessDayPolicy = z.infer<typeof nonBusinessDayPolicySchema>

export const OCCURRENCE_STATUSES = [
  'agendada',
  'realizada',
  'adiada',
  'cancelada',
  'nao_realizada',
] as const
export const occurrenceStatusSchema = /* @__PURE__ */ z.enum(OCCURRENCE_STATUSES)
export type OccurrenceStatus = z.infer<typeof occurrenceStatusSchema>

/**
 * Identidade visual de uma categoria da agenda (SDD 09).
 *
 * É a CHAVE do token, nunca a cor: `packages/ui/src/styles/tokens.css` define
 * o valor em claro e escuro. Guardar hex no banco quebraria o modo escuro e o
 * dia em que o ecossistema reafinar a paleta.
 */
export const AGENDA_CATEGORY_COLORS = [
  'indigo',
  'violet',
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'amber',
  'orange',
  'red',
  'pink',
  'slate',
] as const
export const agendaCategoryColorSchema = /* @__PURE__ */ z.enum(AGENDA_CATEGORY_COLORS)
export type AgendaCategoryColor = z.infer<typeof agendaCategoryColorSchema>

/** Visibilidade de um compromisso (SDD 09 R7/R8). */
export const EVENT_VISIBILITIES = ['publico', 'ocupado', 'privado'] as const
export const eventVisibilitySchema = /* @__PURE__ */ z.enum(EVENT_VISIBILITIES)

export const ATTENDANCE_STATUSES = [
  'confirmado',
  'presente',
  'ausente',
  'justificado',
] as const
export const attendanceStatusSchema = /* @__PURE__ */ z.enum(ATTENDANCE_STATUSES)
export type AttendanceStatus = z.infer<typeof attendanceStatusSchema>

// --- Indicadores -------------------------------------------------------------

/** Q24 — manual pendente não bloqueia os demais; envio parcial é permitido. */
export const INDICATOR_VALUE_STATUSES = [
  'calculado',
  'pendente',
  'aprovado',
  'enviado',
  'falhou',
] as const
export const indicatorValueStatusSchema = /* @__PURE__ */ z.enum(INDICATOR_VALUE_STATUSES)
export type IndicatorValueStatus = z.infer<typeof indicatorValueStatusSchema>

// --- Financeiro --------------------------------------------------------------

/** Herdado do SYS: `sys_currency`. O cálculo oficial é sempre em BRL. */
export const CURRENCIES = ['BRL', 'USD'] as const
export const currencySchema = /* @__PURE__ */ z.enum(CURRENCIES)
export type Currency = z.infer<typeof currencySchema>

/** Q54 — rateio de despesa; percentuais devem somar 100%. */
export const COST_CENTER_KINDS = ['projeto', 'sistema', 'departamento'] as const
export const costCenterKindSchema = /* @__PURE__ */ z.enum(COST_CENTER_KINDS)
export type CostCenterKind = z.infer<typeof costCenterKindSchema>

// --- Infraestrutura ----------------------------------------------------------

export const ERROR_GROUP_STATUSES = [
  'novo',
  'reconhecido',
  'ignorado',
  'resolvido',
] as const
export const errorGroupStatusSchema = /* @__PURE__ */ z.enum(ERROR_GROUP_STATUSES)
export type ErrorGroupStatus = z.infer<typeof errorGroupStatusSchema>

// --- Núcleo transversal ------------------------------------------------------

export const JOB_STATUSES = [
  'pendente',
  'processando',
  'concluido',
  'falhou',
  'dead_letter',
] as const
export const jobStatusSchema = /* @__PURE__ */ z.enum(JOB_STATUSES)
export type JobStatus = z.infer<typeof jobStatusSchema>

export const OUTBOX_STATUSES = [
  'pendente',
  'enviando',
  'enviado',
  'falhou',
  'dead_letter',
] as const
export const outboxStatusSchema = /* @__PURE__ */ z.enum(OUTBOX_STATUSES)
export type OutboxStatus = z.infer<typeof outboxStatusSchema>

// --- Caderno (C11) -----------------------------------------------------------
// Espelham os tipos da migration 023. Mudou lá, muda aqui — o teste do
// caderno.ts confere que todo valor tem rótulo.

/** Tipo da nota: documento de texto (Tiptap) ou quadro de desenho (Excalidraw). */
export const NOTE_KINDS = ['texto', 'quadro'] as const
export const noteKindSchema = /* @__PURE__ */ z.enum(NOTE_KINDS)
export type NoteKind = z.infer<typeof noteKindSchema>

/** ADR-0008 — só `privada` até a etapa P4; o banco recusa as outras antes disso. */
export const NOTE_VISIBILITIES = ['privada', 'compartilhada', 'projeto'] as const
export const noteVisibilitySchema = /* @__PURE__ */ z.enum(NOTE_VISIBILITIES)
export type NoteVisibility = z.infer<typeof noteVisibilitySchema>

/** 20-R7 — `mencao` nasce e morre com o texto; `manual` e `origem`, não. */
export const NOTE_LINK_SOURCES = ['manual', 'mencao', 'origem'] as const
export const noteLinkSourceSchema = /* @__PURE__ */ z.enum(NOTE_LINK_SOURCES)
export type NoteLinkSource = z.infer<typeof noteLinkSourceSchema>

/** Alvo de um vínculo — uma coluna com FK real para cada um no banco. */
export const NOTE_LINK_KINDS = ['pessoa', 'projeto', 'atividade', 'ocorrencia'] as const
export const noteLinkKindSchema = /* @__PURE__ */ z.enum(NOTE_LINK_KINDS)
export type NoteLinkKind = z.infer<typeof noteLinkKindSchema>

/** Recortes que `sistemas.list_notes` aceita. */
export const NOTE_LIST_SCOPES = [
  'todas',
  'caixa_entrada',
  'fixadas',
  'lixeira',
  // P4 (migration 028): notas de outras pessoas abertas para mim.
  'compartilhadas',
  'pessoa',
  'projeto',
  'atividade',
  'ocorrencia',
] as const
export const noteListScopeSchema = /* @__PURE__ */ z.enum(NOTE_LIST_SCOPES)

/** Como quem chama alcança a nota (migration 028, `note_access`). */
export const NOTE_ACCESS_LEVELS = ['autor', 'edicao', 'leitura'] as const
export const noteAccessSchema = /* @__PURE__ */ z.enum(NOTE_ACCESS_LEVELS)
export type NoteAccess = z.infer<typeof noteAccessSchema>
export type NoteListScope = z.infer<typeof noteListScopeSchema>

/** 20-R11 — por que a revisão foi tirada. */
export const NOTE_REVISION_REASONS = ['checkpoint', 'fechamento', 'antes_de_restaurar'] as const
export const noteRevisionReasonSchema = /* @__PURE__ */ z.enum(NOTE_REVISION_REASONS)
export type NoteRevisionReason = z.infer<typeof noteRevisionReasonSchema>
